import { DIRECTIONS, dx, dy, opposite, turnLeft, turnRight, type Direction } from './direction';
import type { Rng } from './rng';
import type { HerdSpec } from './level';

export type CritterKind = 'squirrel' | 'rabbit' | 'dog' | 'sheep' | 'deer' | 'fox' | 'stag';
export const CRITTER_KINDS: readonly CritterKind[] = [
  'squirrel',
  'rabbit',
  'dog',
  'sheep',
  'deer',
  'fox',
  'stag',
];

/** Woodland wildlife (Rolling Meadows): bolts from the mower and runs on for a few bounds. */
const BOUNDS: Partial<Record<CritterKind, number>> = { deer: 2, fox: 1 };
/** Deer graze together: one that takes fright startles every deer this near (Manhattan). */
const HERD_ALARM = 3;

export interface CritterSpawn {
  kind: CritterKind;
  x: number;
  y: number;
}

interface Behavior {
  /** Max Manhattan distance from the spawn ("home") tile. */
  range: number;
  pauseMin: number;
  pauseMax: number;
  hopsMin: number;
  hopsMax: number;
  /** Ticks between hops within a burst. */
  hopGap: number;
  /** Rabbits keep running the same way; squirrels zig-zag. */
  straight: boolean;
  /** Ticks between noticing the mower and dashing away. */
  react: number;
}

const BEHAVIOR: Record<CritterKind, Behavior> = {
  squirrel: {
    range: 3,
    pauseMin: 30,
    pauseMax: 90,
    hopsMin: 1,
    hopsMax: 2,
    hopGap: 10,
    straight: false,
    react: 6,
  },
  rabbit: {
    range: 4,
    pauseMin: 70,
    pauseMax: 150,
    hopsMin: 2,
    hopsMax: 4,
    hopGap: 12,
    straight: true,
    react: 3,
  },
  // Deer graze in little groups, drifting slowly; startled, they bound away together.
  deer: {
    range: 5,
    pauseMin: 100,
    pauseMax: 220,
    hopsMin: 1,
    hopsMax: 2,
    hopGap: 14,
    straight: true,
    react: 4,
  },
  // Foxes trot about quick and low, darting this way and that.
  fox: {
    range: 5,
    pauseMin: 40,
    pauseMax: 100,
    hopsMin: 2,
    hopsMax: 4,
    hopGap: 7,
    straight: false,
    react: 2,
  },
  // The stag grazes slowly and stays near; startled, it leaves the field (see leave).
  stag: {
    range: 3,
    pauseMin: 140,
    pauseMax: 280,
    hopsMin: 1,
    hopsMax: 1,
    hopGap: 12,
    straight: true,
    react: 5,
  },
  // Sheep have their own routine (see updateSheep).
  sheep: {
    range: 99,
    pauseMin: 90,
    pauseMax: 200,
    hopsMin: 1,
    hopsMax: 1,
    hopGap: 0,
    straight: false,
    react: 0,
  },
  // The dog has its own routine (see updateDog); only its range is used from here.
  dog: {
    range: 3,
    pauseMin: 0,
    pauseMax: 0,
    hopsMin: 0,
    hopsMax: 0,
    hopGap: 0,
    straight: true,
    react: 0,
  },
};

/**
 * The dog (lives in a dog house; its spawn tile is the doorway): naps inside, trots out for a short
 * wander near home, then walks back in. It ignores the mower; bumping it costs a hit like any
 * critter, and it (or the bike horn) sends it trotting home. Never harmed.
 */
const DOG = {
  /** Ticks between steps while wandering, and while trotting home. */
  step: 18,
  trot: 10,
  walksMin: 3,
  walksMax: 6,
  napMin: 240,
  napMax: 480,
  /** First outing comes sooner. */
  firstMin: 60,
  firstMax: 240,
} as const;

export interface CritterState {
  kind: CritterKind;
  x: number;
  y: number;
  homeX: number;
  homeY: number;
  dir: Direction;
  /** Ticks until the next action. */
  timer: number;
  hopsLeft: number;
  /** Tick of the most recent hop (for renderers). */
  lastHopTick: number;
  /** Noticed the mower; dashes away when the timer runs out. */
  alarmed: boolean;
  /** The dog: indoors (not on the lawn at all). */
  inside: boolean;
  /** The dog: wander steps left on this outing. */
  walkLeft: number;
  /** The dog: heading back in. */
  homeward: boolean;
  /** Sheep: in the pen (stays there). */
  penned: boolean;
  /** Sheep: which pen it is in (a round-up has one per seat; -1 while loose). */
  pen: number;
  /** Sent home by the bike horn: off the lawn for this many more ticks. */
  awayTicks: number;
  /** The stag: startled, it is making for the nearest edge of the lawn. */
  leaving: boolean;
  /** The stag: left the field for good. */
  gone: boolean;
}

/** What the world lets a critter do; supplied by the simulation each tick. */
export interface CritterWorld {
  /** Terrain a critter may stand on (not obstacles, brush, or the fence). */
  canStand(x: number, y: number): boolean;
  /** Open 4-neighbors of a tile, used to keep critters out of corridors and dead ends. */
  openNeighbors(x: number, y: number): number;
  /** Tiles near the mower that critters must never hop into (the fairness rule). */
  forbidden(x: number, y: number): boolean;
  /** Where the mower is about to be (its next decision tile); critters flee from it. */
  threat(): { x: number; y: number };
  /** Herding: the pens and their gates (the boss has one; a round-up match one per seat). */
  pens: readonly HerdSpec[];
  /**
   * A match: where every mower is about to be (sheep graze while any is near). Without it, just
   * {@link threat}.
   */
  threats?(): readonly { x: number; y: number }[];
  /** A match: another mower than the one nudging holds (x, y), so a sheep can't be pushed there. */
  mowerIn?(x: number, y: number): boolean;
  /** The lawn's size (the stag leaves by its nearest edge). */
  width: number;
  height: number;
}

/** Critters notice the mower within this Manhattan distance of its next decision tile. */
export const ALARM_RADIUS = 2;

/** Minimum open neighbors for a tile a critter will hop into: keeps them out of 1-wide lanes. */
const MIN_OPENNESS = 3;

/**
 * Deterministic critters. All randomness comes from the sim's seeded RNG, consumed in a fixed
 * order, so the same level + the same mower path always produce the same critter movement.
 */
export class Critters {
  readonly list: CritterState[];

  constructor(
    spawns: readonly CritterSpawn[],
    private readonly rng: Rng,
  ) {
    this.list = spawns.map((s) => {
      const b = BEHAVIOR[s.kind];
      const dog = s.kind === 'dog';
      return {
        kind: s.kind,
        x: s.x,
        y: s.y,
        homeX: s.x,
        homeY: s.y,
        dir: rng.pick(DIRECTIONS),
        timer: dog
          ? rng.intBetween(DOG.firstMin, DOG.firstMax)
          : rng.intBetween(b.pauseMin, b.pauseMax),
        hopsLeft: 0,
        lastHopTick: 0,
        alarmed: false,
        inside: dog,
        walkLeft: 0,
        homeward: false,
        penned: false,
        pen: -1,
        awayTicks: 0,
        leaving: false,
        gone: false,
      };
    });
  }

  /** On the lawn (not indoors, not sent home). */
  private static present(c: CritterState): boolean {
    return !c.inside && c.awayTicks === 0 && !c.gone;
  }

  occupied(x: number, y: number): boolean {
    return this.list.some((c) => Critters.present(c) && c.x === x && c.y === y);
  }

  indexAt(x: number, y: number): number {
    return this.list.findIndex((c) => Critters.present(c) && c.x === x && c.y === y);
  }

  /**
   * The bike horn: every critter but the sheep heads home and stays off the lawn for `ticks` (the
   * dog goes into its house). Returns the indices sent.
   */
  sendHome(ticks: number, tick: number): number[] {
    const sent: number[] = [];
    this.list.forEach((c, i) => {
      if (c.kind === 'sheep' || c.leaving || !Critters.present(c)) return;
      c.lastHopTick = tick; // renderers: it's on the move
      if (c.kind === 'dog') {
        // Indoors for a nap as long as the horn's quiet, then out as usual.
        c.inside = true;
        c.homeward = false;
        c.x = c.homeX;
        c.y = c.homeY;
        c.timer = ticks;
      } else {
        c.awayTicks = ticks;
        c.alarmed = false;
      }
      sent.push(i);
    });
    return sent;
  }

  /**
   * A squirrel or rabbit bumped (rules 6): gone from the lawn in a puff for `ticks`, then back to
   * where it started, like one sent home by the horn.
   */
  poof(index: number, ticks: number, tick: number): void {
    const c = this.list[index];
    if (!c) return;
    c.awayTicks = ticks;
    c.alarmed = false;
    void tick; // (its puff is drawn from the sim's event, so no hop to animate)
  }

  /** A critter that was sent home comes back to where it started, once that tile is free. */
  private comeBack(c: CritterState, tick: number, world: CritterWorld): void {
    if (c.awayTicks > 1) {
      c.awayTicks--;
      return;
    }
    // Still away while checking (so it doesn't count as standing on its own home tile).
    const { homeX: x, homeY: y } = c;
    if (!world.canStand(x, y) || this.occupied(x, y) || world.forbidden(x, y)) return;
    c.awayTicks = 0;
    c.x = x;
    c.y = y;
    c.lastHopTick = tick;
    c.timer = BEHAVIOR[c.kind].pauseMax;
  }

  /** Advances all critters one tick; returns the indices of critters that dashed away startled. */
  update(tick: number, world: CritterWorld): number[] {
    const fled: number[] = [];
    const threat = world.threat();
    for (let i = 0; i < this.list.length; i++) {
      const c = this.list[i] as CritterState;
      if (c.kind === 'dog') {
        this.updateDog(c, tick, world);
        continue;
      }
      if (c.kind === 'sheep') {
        this.updateSheep(c, tick, world);
        continue;
      }
      if (c.gone) continue;
      if (c.awayTicks > 0) {
        this.comeBack(c, tick, world);
        continue;
      }
      const b = BEHAVIOR[c.kind];
      if (c.leaving) {
        if (--c.timer <= 0) this.leave(c, tick, world);
        continue;
      }
      // Skittish: notice an approaching mower and dash off after a short reaction delay.
      if (!c.alarmed && Math.abs(c.x - threat.x) + Math.abs(c.y - threat.y) <= ALARM_RADIUS) {
        c.alarmed = true;
        c.timer = Math.min(c.timer, b.react);
        if (c.kind === 'deer') this.startleHerd(c);
      }
      if (--c.timer > 0) continue;
      if (c.alarmed) {
        c.alarmed = false;
        if (c.kind === 'stag') {
          // The stag won't be chased about: it makes for the edge of the field and leaves.
          c.leaving = true;
          this.leave(c, tick, world);
          fled.push(i);
          continue;
        }
        if (this.shoo(i, threat.x, threat.y, tick, world)) {
          fled.push(i);
          // Wildlife keeps running a few bounds the same way.
          const bounds = BOUNDS[c.kind] ?? 0;
          if (bounds > 0) {
            c.hopsLeft = bounds;
            c.timer = b.hopGap;
          }
        } else c.timer = b.hopGap; // cornered: try again shortly
        continue;
      }
      if (c.hopsLeft === 0) {
        // Start a new burst after this pause.
        c.hopsLeft = this.rng.intBetween(b.hopsMin, b.hopsMax);
        c.dir = this.rng.pick(DIRECTIONS);
        c.timer = b.hopGap;
        continue;
      }
      c.hopsLeft--;
      c.timer = c.hopsLeft > 0 ? b.hopGap : this.rng.intBetween(b.pauseMin, b.pauseMax);
      const options = this.hopOptions(c, world);
      if (options.length === 0) continue;
      const dir = b.straight && options.includes(c.dir) ? c.dir : this.rng.pick(options);
      this.hop(c, dir, tick);
    }
    return fled;
  }

  /** A deer takes fright: so does every deer grazing near it (they bolt together). */
  private startleHerd(c: CritterState): void {
    for (const o of this.list) {
      if (o === c || o.kind !== 'deer' || o.alarmed || !Critters.present(o)) continue;
      if (Math.abs(o.x - c.x) + Math.abs(o.y - c.y) > HERD_ALARM) continue;
      o.alarmed = true;
      o.timer = Math.min(o.timer, BEHAVIOR.deer.react + 2);
    }
  }

  /**
   * The stag leaving: a stride toward the nearest edge of the lawn, then off it and gone for good
   * (it waits if the way is blocked, and never steps toward the mower's next tile).
   */
  private leave(c: CritterState, tick: number, world: CritterWorld): void {
    c.timer = BEHAVIOR.stag.hopGap;
    if (this.edgeDistance(c.x, c.y, world) === 0) {
      // On the edge: its next stride takes it off the field.
      c.gone = true;
      c.lastHopTick = tick;
      return;
    }
    const options = DIRECTIONS.filter((d) => {
      const x = c.x + dx(d);
      const y = c.y + dy(d);
      return world.canStand(x, y) && !this.occupied(x, y) && !world.forbidden(x, y);
    });
    if (options.length === 0) return;
    // The open stride that brings it nearest the edge (a sideways one if it must, to get round).
    const after = (d: Direction) => this.edgeDistance(c.x + dx(d), c.y + dy(d), world);
    this.hop(
      c,
      options.reduce((a, d) => (after(d) < after(a) ? d : a)),
      tick,
    );
  }

  /** Tiles from (x, y) to the nearest edge of the lawn. */
  private edgeDistance(x: number, y: number, world: CritterWorld): number {
    return Math.min(x, y, world.width - 1 - x, world.height - 1 - y);
  }

  /**
   * Sheep (boss herding): calm. They graze where they are while the mower is near (so it can line
   * up a nudge), drift a tile now and then when it is far, and once in the pen stay in it.
   */
  private updateSheep(c: CritterState, tick: number, world: CritterWorld): void {
    if (--c.timer > 0) return;
    const b = BEHAVIOR.sheep;
    c.timer = this.rng.intBetween(b.pauseMin, b.pauseMax);
    const threats = world.threats?.() ?? [world.threat()];
    const near = threats.some((t) => Math.abs(c.x - t.x) + Math.abs(c.y - t.y) <= 4);
    if (!c.penned && near) return;
    const pen = world.pens[c.pen]?.pen ?? [];
    const options = DIRECTIONS.filter((d) => {
      const x = c.x + dx(d);
      const y = c.y + dy(d);
      if (this.occupied(x, y)) return false;
      if (c.penned) return pen.some((p) => p.x === x && p.y === y);
      if (world.pens.some(({ gate }) => gate.x === x && gate.y === y)) return false;
      return world.canStand(x, y) && !world.forbidden(x, y) && world.openNeighbors(x, y) >= 2;
    });
    if (options.length === 0) return;
    this.hop(c, this.rng.pick(options), tick);
  }

  /**
   * Boss herding: the mower nudges the sheep at (x, y) one tile on in `dir`, if there is room.
   * Returns whether it moved (and whether it ended up in the pen).
   */
  nudge(
    x: number,
    y: number,
    dir: Direction,
    tick: number,
    world: CritterWorld,
  ): { moved: boolean; penned: boolean; index: number } {
    const index = this.indexAt(x, y);
    const c = this.list[index];
    if (!c || c.kind !== 'sheep' || c.penned) return { moved: false, penned: false, index };
    // On ahead if it can; cornered, it is forced out to the side, or squeezes back past the
    // mower (onto the tile the mower is leaving). A sheep is never a crash. In a match, never
    // onto another mower.
    const gateAt = (tx: number, ty: number) =>
      world.pens.findIndex(({ gate }) => gate.x === tx && gate.y === ty);
    const way = [dir, turnLeft(dir), turnRight(dir), opposite(dir)].find((d) => {
      const tx = x + dx(d);
      const ty = y + dy(d);
      if (world.mowerIn?.(tx, ty)) return false;
      return gateAt(tx, ty) >= 0 || (world.canStand(tx, ty) && !this.occupied(tx, ty));
    });
    if (way === undefined) return { moved: false, penned: false, index };
    const nx = x + dx(way);
    const ny = y + dy(way);
    c.timer = Math.max(c.timer, BEHAVIOR.sheep.pauseMin);
    const penIndex = gateAt(nx, ny);
    const herd = world.pens[penIndex];
    if (herd) {
      // Through the gate: trots into the free pen spot nearest it.
      let spot: { x: number; y: number } | null = null;
      let best = Infinity;
      for (const p of herd.pen) {
        const d = Math.abs(p.x - nx) + Math.abs(p.y - ny);
        if (d < best && !this.occupied(p.x, p.y)) {
          best = d;
          spot = p;
        }
      }
      if (!spot) return { moved: false, penned: false, index };
      c.x = spot.x;
      c.y = spot.y;
      c.dir = way;
      c.lastHopTick = tick;
      c.penned = true;
      c.pen = penIndex;
      return { moved: true, penned: true, index };
    }
    this.hop(c, way, tick);
    return { moved: true, penned: false, index };
  }

  private updateDog(c: CritterState, tick: number, world: CritterWorld): void {
    if (--c.timer > 0) return;
    const home = { x: c.homeX, y: c.homeY };
    if (c.inside) {
      // Out for a wander once the doorway is clear (and not right in front of the mower).
      c.timer = DOG.step;
      if (!world.canStand(home.x, home.y) || this.occupied(home.x, home.y)) return;
      if (world.forbidden(home.x, home.y)) return;
      c.inside = false;
      c.homeward = false;
      c.walkLeft = this.rng.intBetween(DOG.walksMin, DOG.walksMax);
      c.lastHopTick = tick;
      return;
    }
    if (!c.homeward) {
      c.timer = DOG.step;
      const options = this.hopOptions(c, world);
      if (options.length > 0) {
        // Mostly keeps trotting the same way.
        const dir =
          options.includes(c.dir) && this.rng.chance(2, 3) ? c.dir : this.rng.pick(options);
        this.hop(c, dir, tick);
      }
      if (--c.walkLeft <= 0) c.homeward = true;
      return;
    }
    // Heading home.
    c.timer = DOG.trot;
    if (c.x === home.x && c.y === home.y) {
      c.inside = true;
      c.homeward = false;
      c.timer = this.rng.intBetween(DOG.napMin, DOG.napMax);
      c.lastHopTick = tick;
      return;
    }
    const options = this.hopOptions(c, world, true);
    if (options.length === 0) return; // blocked for now: wait
    const distance = (d: Direction) =>
      Math.abs(c.x + dx(d) - home.x) + Math.abs(c.y + dy(d) - home.y);
    const best = options.reduce((a, d) => (distance(d) < distance(a) ? d : a));
    if (distance(best) < Math.abs(c.x - home.x) + Math.abs(c.y - home.y)) this.hop(c, best, tick);
  }

  /** Startled: dash to the free neighbor farthest from (fromX, fromY). The dog trots home. */
  shoo(index: number, fromX: number, fromY: number, tick: number, world: CritterWorld): boolean {
    const c = this.list[index];
    if (!c || c.inside) return false;
    if (c.kind === 'dog') {
      c.homeward = true;
      c.walkLeft = 0;
      c.timer = DOG.trot;
      return true;
    }
    const options = this.hopOptions(c, world, true);
    if (options.length === 0) return false;
    const distance = (d: Direction) =>
      Math.abs(c.x + dx(d) - fromX) + Math.abs(c.y + dy(d) - fromY);
    const best = options.reduce((a, d) => (distance(d) > distance(a) ? d : a));
    this.hop(c, best, tick);
    c.hopsLeft = 0;
    c.timer = BEHAVIOR[c.kind].pauseMin;
    return true;
  }

  private hopOptions(c: CritterState, world: CritterWorld, fleeing = false): Direction[] {
    const b = BEHAVIOR[c.kind];
    return DIRECTIONS.filter((d) => {
      const x = c.x + dx(d);
      const y = c.y + dy(d);
      if (!world.canStand(x, y) || this.occupied(x, y) || world.forbidden(x, y)) return false;
      // The dog may always step into its own doorway, however snug.
      const doorway = c.kind === 'dog' && x === c.homeX && y === c.homeY;
      if (!doorway && world.openNeighbors(x, y) < MIN_OPENNESS) return false;
      // A fleeing critter may leave its home range; a wandering one may not.
      return fleeing || Math.abs(x - c.homeX) + Math.abs(y - c.homeY) <= b.range;
    });
  }

  private hop(c: CritterState, dir: Direction, tick: number): void {
    c.x += dx(dir);
    c.y += dy(dir);
    c.dir = dir;
    c.lastHopTick = tick;
  }
}
